/**
 * The admin panel (F9).
 *
 * Four areas behind tabs: the taxonomy vocabularies, the scorecard weights and
 * thresholds, the users, and the demo data controls. Admin-only — the route
 * guard keeps a non-admin out, and the API enforces it regardless (PRD §6.2).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 7.13, 7.14, PRD §5, F9
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHead } from '../../components/ui/PageHead';
import { TabPanel } from '../../components/ui/tab-panel';
import { Tabs } from '../../components/ui/Tabs';

import { DemoControls } from './components/DemoControls';
import { SettingsManager } from './components/SettingsManager';
import { TaxonomyManager } from './components/TaxonomyManager';
import { UserManager } from './components/UserManager';

type AdminTab = 'taxonomy' | 'settings' | 'users' | 'demo';

export function AdminPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<AdminTab>('taxonomy');

  const tabs = [
    { value: 'taxonomy', label: t('admin.tabs.taxonomy') },
    { value: 'settings', label: t('admin.tabs.settings') },
    { value: 'users', label: t('admin.tabs.users') },
    { value: 'demo', label: t('admin.tabs.demo') },
  ];

  return (
    <>
      <PageHead
        kicker={t('nav.group.insight')}
        title={t('page.admin')}
        lead={t('admin.lead')}
      />

      <Tabs
        tabs={tabs}
        value={tab}
        onValueChange={(value) => {
          setTab(value as AdminTab);
        }}
        ariaLabel={t('admin.tabsLabel')}
      >
        <TabPanel value="taxonomy" className="pt-s4">
          <TaxonomyManager />
        </TabPanel>
        <TabPanel value="settings" className="pt-s4">
          <SettingsManager />
        </TabPanel>
        <TabPanel value="users" className="pt-s4">
          <UserManager />
        </TabPanel>
        <TabPanel value="demo" className="pt-s4">
          <DemoControls />
        </TabPanel>
      </Tabs>
    </>
  );
}
